import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO, isThisWeek, isThisMonth, differenceInDays } from 'date-fns';
import { CheckCircle2, Calendar, BookOpen, Trophy, TrendingUp, Clock } from 'lucide-react';
import { getTasks, getSubjects } from '../utils/storage';

const History = () => {
  const [tasks, setTasks] = useState([]);
  const [subjects, setSubjects] = useState({});
  const [groupedTasks, setGroupedTasks] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    const tasksData = getTasks();
    const subjectsData = getSubjects();
    const subjectsMap = subjectsData.reduce((acc, subj) => {
      acc[subj.id] = subj;
      return acc;
    }, {});

    setSubjects(subjectsMap);
    const completedTasks = tasksData
      .filter(task => task.completed)
      .map(task => ({
        ...task,
        // Ensure we have a valid date for sorting and grouping
        completedAt: task.completedAt || task.createdAt || new Date().toISOString()
      }))
      .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));

    // Group tasks by time periods
    const grouped = groupTasksByTime(completedTasks);
    setGroupedTasks(grouped);
    setTasks(completedTasks);
  };

  const groupTasksByTime = (tasks) => {
    const groups = {
      today: [],
      yesterday: [],
      thisWeek: [],
      thisMonth: [],
      older: []
    };

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthAgo = new Date(today);
    monthAgo.setMonth(monthAgo.getMonth() - 1);

    tasks.forEach(task => {
      // Use the ensured completedAt date (which is now guaranteed to exist)
      const completedDate = parseISO(task.completedAt);
      const taskDate = new Date(completedDate.getFullYear(), completedDate.getMonth(), completedDate.getDate());

      if (taskDate.getTime() === today.getTime()) {
        groups.today.push(task);
      } else if (taskDate.getTime() === yesterday.getTime()) {
        groups.yesterday.push(task);
      } else if (taskDate >= weekAgo) {
        groups.thisWeek.push(task);
      } else if (taskDate >= monthAgo) {
        groups.thisMonth.push(task);
      } else {
        groups.older.push(task);
      }
    });

    return groups;
  };

  const getGroupIcon = (groupKey) => {
    switch (groupKey) {
      case 'today': return <Trophy size={20} />;
      case 'yesterday': return <CheckCircle2 size={20} />;
      case 'thisWeek': return <TrendingUp size={20} />;
      case 'thisMonth': return <Calendar size={20} />;
      default: return <Clock size={20} />;
    }
  };

  const getGroupTitle = (groupKey) => {
    switch (groupKey) {
      case 'today': return 'Today';
      case 'yesterday': return 'Yesterday';
      case 'thisWeek': return 'This Week';
      case 'thisMonth': return 'This Month';
      default: return 'Older';
    }
  };

  const getStats = () => {
    const total = tasks.length;
    const thisWeek = tasks.filter(task => {
      const completedDate = parseISO(task.completedAt);
      return isThisWeek(completedDate);
    }).length;

    const thisMonth = tasks.filter(task => {
      const completedDate = parseISO(task.completedAt);
      return isThisMonth(completedDate);
    }).length;

    return { total, thisWeek, thisMonth };
  };

  const stats = getStats();

  return (
    <div className="container">
      <div className="header-section">
        <h1>Task History</h1>
        <p className="form-subtitle">Your journey of completed tasks and achievements</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <Trophy size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">Total Completed</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.thisWeek}</div>
            <div className="stat-label">This Week</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <Calendar size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.thisMonth}</div>
            <div className="stat-label">This Month</div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      {tasks.length > 0 ? (
        <div className="timeline">
          {Object.entries(groupedTasks).map(([groupKey, groupTasks]) => {
            if (groupTasks.length === 0) return null;

            return (
              <div key={groupKey} className="timeline-group">
                <div className="timeline-header">
                  <div className="timeline-icon">
                    {getGroupIcon(groupKey)}
                  </div>
                  <h3 className="timeline-title">{getGroupTitle(groupKey)}</h3>
                  <span className="timeline-count">{groupTasks.length}</span>
                </div>

                <div className="timeline-items">
                  {groupTasks.map(task => {
                    const subject = subjects[task.subjectId];
                    const completedDate = parseISO(task.completedAt);
                    const daysAgo = differenceInDays(new Date(), completedDate);

                    return (
                      <div key={task.id} className="timeline-item">
                        <div className="timeline-marker">
                          <CheckCircle2 size={16} className="timeline-check" />
                        </div>

                        <div className="timeline-content">
                          <div className="timeline-card">
                            <div className="timeline-header-content">
                              <h4 className="timeline-task-title">{task.title}</h4>
                              <div className="timeline-meta">
                                {subject && (
                                  <span
                                    className="timeline-subject-badge"
                                    style={{ backgroundColor: subject.color + '20', color: subject.color }}
                                  >
                                    <BookOpen size={12} />
                                    {subject.name}
                                  </span>
                                )}
                                <span className="timeline-date">
                                  <Calendar size={12} />
                                  {format(completedDate, 'MMM dd, yyyy')}
                                  {daysAgo > 0 && ` (${daysAgo} days ago)`}
                                </span>
                              </div>
                            </div>

                            {task.description && (
                              <p className="timeline-description">{task.description}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-illustration">
            <Trophy size={64} strokeWidth={1} />
          </div>
          <h2>No completed tasks yet</h2>
          <p>Your task completion history will appear here. Start completing tasks to build your achievement timeline!</p>
          <Link to="/">
            <button className="add-button">
              <CheckCircle2 size={20} />
              View Active Tasks
            </button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default History;
